import React, { useState } from 'react';

import { Autocomplete, TextField, Typography } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { AlineCheckbox, FilterTags } from 'src/components';
import { StatusDot } from 'src/components/reports/report-detail/StatusDot';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import {
  filterAutocompleteStyle,
  usePreserveAutocompleteSearchInput,
} from './autocomplete-filter-utils';

const useStyles = makeStyles()(theme => ({
  autocomplete: filterAutocompleteStyle,
  item: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

const formatStatusName = (status: Apollo.RowStatus): string => {
  switch (status) {
    case Apollo.RowStatus.AiNotRun:
      return 'AI Not Run';
    case Apollo.RowStatus.AiError:
      return 'AI Error';
    case Apollo.RowStatus.OcrError:
      return 'OCR Error';
    case Apollo.RowStatus.AiRunning:
      return 'AI Thinking';
    case Apollo.RowStatus.AnswerStale:
      return 'Answer Stale';
    case Apollo.RowStatus.OcrRunning:
      return 'OCR Running';
  }
};

export function RowStatusAutocompleteFilter({
  applyValue,
  item,
}: GridFilterInputValueProps): JSX.Element {
  const { classes } = useStyles();
  const [truncateTags, setTruncateTags] = useState(true);
  const [values, setValues] = useState<Apollo.RowStatus[]>(item.value ?? []);
  const { inputValue, onInputChange } = usePreserveAutocompleteSearchInput();

  function handleValueChange(value: Apollo.RowStatus[]): void {
    applyValue({
      ...item,
      value,
    });
    setValues(value);
  }

  return (
    <Autocomplete<Apollo.RowStatus, true, false, false>
      disableCloseOnSelect
      fullWidth
      multiple
      openOnFocus
      blurOnSelect={false}
      className={classes.autocomplete}
      getOptionLabel={option => formatStatusName(option)}
      inputValue={inputValue}
      options={Object.values(Apollo.RowStatus).sort((a, b) => a.localeCompare(b))}
      renderInput={params => {
        return <TextField placeholder="Search statuses..." {...params} variant="standard" />;
      }}
      renderOption={(props, option, state) => {
        const { className, key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps} className={clsx(className, classes.item)}>
            <AlineCheckbox checked={state.selected} />
            <StatusDot status={option} />
            <Typography>{formatStatusName(option)}</Typography>
          </li>
        );
      }}
      renderValue={tags => {
        return (
          <FilterTags<Apollo.RowStatus>
            getTextFromTag={tag => formatStatusName(tag)}
            tags={tags}
            truncateTags={truncateTags}
            onDelete={tag => handleValueChange(tags.filter(filterTag => tag !== filterTag))}
          />
        );
      }}
      slotProps={{
        popper: {
          modifiers: [
            {
              enabled: false,
              name: 'flip',
            },
            {
              enabled: false,
              name: 'preventOverflow',
            },
          ],
        },
      }}
      value={values}
      onBlur={e => {
        e.stopPropagation();
        setTruncateTags(true);
      }}
      onChange={(_e, values) => handleValueChange(values)}
      onFocus={e => {
        e.stopPropagation();
        setTruncateTags(false);
      }}
      onInputChange={onInputChange}
    />
  );
}
